import React, { useMemo, useState } from "react";
import {
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
} from "@mui/material";
import { useGetTicketTagSlReportQuery } from "../../../../Services/reports/tickets/ReportTickets";
import { useGetTicketTagSLAEscalationLevelsQuery } from "../../../../Services/settingsAPIs/CommonAPI";
import TicketSlReportShell from "./TicketSlReportShell";
import {
  buildSingleOrMultiParam,
  MenuProps,
  toggleMultiSelect,
} from "./ticketSlReportUtils";

const TicketTagSlReport = () => {
  const [params, setParams] = useState<Record<string, string | number> | null>(
    null,
  );
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);

  const { data: ticketTags, isLoading: tagsLoading } =
    useGetTicketTagSLAEscalationLevelsQuery({});
  const {
    data: reportData,
    isLoading: reportLoading,
    isFetching: reportFetching,
    isError,
    error,
    refetch,
  } = useGetTicketTagSlReportQuery(params, { skip: !params });

  const tagOptions = useMemo(
    () =>
      Array.isArray(ticketTags)
        ? ticketTags
            .map((tag: any) => ({
              id: String(tag.tag_id ?? tag.id ?? ""),
              name: tag.tag ?? tag.tag_name ?? tag.name ?? "Unknown tag",
            }))
            .filter((tag) => tag.id)
        : [],
    [ticketTags],
  );
  const allTagIds = useMemo(() => tagOptions.map((tag) => tag.id), [tagOptions]);
  const isAllTagsSelected =
    allTagIds.length > 0 && selectedTagIds.length === allTagIds.length;

  const buildExtraParams = () => {
    if (!allTagIds.length) return null;
    return buildSingleOrMultiParam(
      selectedTagIds,
      allTagIds,
      "tag_id",
      "tag_ids",
    );
  };

  const extraFilters = (
    <div className="w-100 mw-200px">
      <FormControl fullWidth>
        <InputLabel id="tag-select-label">Customer tag</InputLabel>
        <Select
          labelId="tag-select-label"
          multiple
          value={selectedTagIds}
          input={<OutlinedInput label="Customer tag" />}
          renderValue={(selected) => {
            if (isAllTagsSelected || selected.length === 0) {
              return <div>All</div>;
            }
            const names = tagOptions
              .filter((tag) => selected.includes(tag.id))
              .map((tag) => tag.name);
            return <div>{names.join(", ")}</div>;
          }}
          MenuProps={MenuProps}
        >
          <MenuItem
            value="all"
            onClick={(e) => {
              e.preventDefault();
              toggleMultiSelect("all", setSelectedTagIds, allTagIds);
            }}
          >
            <Checkbox
              checked={isAllTagsSelected}
              indeterminate={selectedTagIds.length > 0 && !isAllTagsSelected}
            />
            <ListItemText primary="All" />
          </MenuItem>
          {tagOptions.map((tag) => (
            <MenuItem
              key={tag.id}
              value={tag.id}
              onClick={(e) => {
                e.preventDefault();
                toggleMultiSelect(tag.id, setSelectedTagIds, allTagIds);
              }}
            >
              <Checkbox checked={selectedTagIds.includes(tag.id)} />
              <ListItemText primary={tag.name} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </div>
  );

  return (
    <TicketSlReportShell
      title="Tag SL"
      downloadFileName="Ticket_Tag_SL_Report"
      errorMessage="Failed to fetch Tag SL report"
      reportData={reportData}
      reportLoading={reportLoading}
      reportFetching={reportFetching}
      isError={isError}
      error={error}
      refetch={refetch}
      params={params}
      setParams={setParams}
      buildExtraParams={buildExtraParams}
      validateExtra={() =>
        !allTagIds.length ? "No customer tags available to filter" : null
      }
      extraFilters={extraFilters}
      extraLoading={tagsLoading}
      onResetExtra={() => setSelectedTagIds([])}
    />
  );
};

export default TicketTagSlReport;
